import { OrgStatus } from "@fieldday/fielddayportal-model"
import { SelectChangeEvent, Dialog, DialogTitle, DialogContent, InputLabel, Select, MenuItem, DialogActions, Button, Typography } from "@mui/material"
import { AlertSeverity, useLoading } from "../../hooks/useLoading"
import { NPO } from "../../models/Npo"
import { OrgUnit } from "../../models/OrgUnit"
import { useAPI } from "../../util/useAPI"

const StatusDialog = (
  { open, setOpen, org, putStatus }: {
    open: boolean,
    setOpen: (open: boolean) => void,
    org: OrgUnit | NPO,
    putStatus: (event: SelectChangeEvent<OrgStatus>) => void
  }
) => {
  // anything can be set except private
  const allStatuses = Object.values(OrgStatus).filter(s => s !== OrgStatus.Private)
  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>Update Status for {org.name}</DialogTitle>
      <DialogContent>
        <form>
          <InputLabel>Status</InputLabel>
          { org.status === OrgStatus.Private ?
            <Typography>Private (cannot be changed)</Typography>
           :
            <Select value={org.status} onChange={putStatus}>
              {allStatuses.map(s => <MenuItem value={s} key={s}>{s}</MenuItem>)}
            </Select>
          }
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Go back</Button>
      </DialogActions>
    </Dialog>
  )
}

export const UpdateTeamStatusForm = (
  { open, setOpen, team, setTeam, teams, setTeams }: {
    open: boolean,
    setOpen: (open: boolean) => void,
    team: OrgUnit | undefined,
    setTeam: (team: OrgUnit | undefined) => void,
    teams?: OrgUnit[],
    setTeams?: (teams: OrgUnit[]) => void
}) => {
  const { setAlert, loadStart, loadEnd } = useLoading()
  const FieldDayAPI = useAPI()

  if (!team) return <></>

  const putStatus = (event: SelectChangeEvent<OrgStatus>) => {
    loadStart(true)
    FieldDayAPI.updateOrgUnitStatus(team.id, event.target.value as OrgStatus)
      .then((resp) => {
        setAlert(AlertSeverity.SUCCESS, resp.message)
        setTeam(resp.orgUnit)
        if (teams && setTeams) { // if there's a list, update in list
          const idx = teams?.findIndex(t => t.id === resp.orgUnit.id)
          teams[idx] = resp.orgUnit
          setTeams(teams)
        }
        setOpen(false)
      })
      .catch(e => {
        setAlert(AlertSeverity.ERROR, e.response?.data?.message || e)
      })
      .finally(loadEnd)
  }

  return <StatusDialog open={open} setOpen={setOpen} org={team} putStatus={putStatus} />
}

export const UpdateNpoStatusForm = (
  { open, setOpen, npo, setNpo, npos, setNpos }: {
    open: boolean,
    setOpen: (open: boolean) => void,
    npo: NPO | undefined,
    setNpo: (team: NPO | undefined) => void,
    npos?: NPO[],
    setNpos?: (teams: NPO[]) => void
}) => {
  const { setAlert, loadStart, loadEnd } = useLoading()
  const FieldDayAPI = useAPI()

  if (!npo) return <></>

  const putStatus = (event: SelectChangeEvent<OrgStatus>) => {
    loadStart(true)
    FieldDayAPI.updateNonprofitStatus(npo.id, event.target.value as OrgStatus)
      .then((resp) => {
        setAlert(AlertSeverity.SUCCESS, resp.message)
        setNpo(resp.nonProfitOrg)
        if (npos && setNpos) { // if there's a list, update in list
          const idx = npos?.findIndex(t => t.id === resp.nonProfitOrg.id)
          npos[idx] = resp.nonProfitOrg
          setNpos(npos)
        }
        setOpen(false)
      })
      .catch(e => {
        setAlert(AlertSeverity.ERROR, e.response?.data?.message || e)
      })
      .finally(loadEnd)
  }

  return <StatusDialog open={open} setOpen={setOpen} org={npo} putStatus={putStatus} />
}
